import { useEffect, useRef, useState } from "react";
import { Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { INSTAGRAM, INSTAGRAM_URL } from "@/lib/constants";

// 🔧 Swap these 3 URLs anytime — paste full Instagram reel/post URLs from @drkhushbumutha
const INSTAGRAM_POSTS = [
  "https://www.instagram.com/p/DXwPJPJoVST/",
  "https://www.instagram.com/p/DXedCx_iD7c/",
  "https://www.instagram.com/p/DSl74XMCIPo/",
];

declare global {
  interface Window {
    instgrm?: { Embeds: { process: () => void } };
  }
}

const InstagramEmbeds = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  // Lazy-load Instagram embed script only when the section scrolls into view.
  useEffect(() => {
    if (!sectionRef.current || visible) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    io.observe(sectionRef.current);
    return () => io.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const SCRIPT_ID = "instagram-embed-script";
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;

    const applyTitles = () => {
      document
        .querySelectorAll<HTMLIFrameElement>('iframe.instagram-media')
        .forEach((f, i) => {
          if (!f.title) f.title = `Instagram post ${i + 1} — @drkhushbumutha`;
        });
    };

    if (existing && window.instgrm) {
      window.instgrm.Embeds.process();
      setTimeout(applyTitles, 1500);
      return;
    }

    if (!existing) {
      const s = document.createElement("script");
      s.id = SCRIPT_ID;
      s.async = true;
      s.src = "https://www.instagram.com/embed.js";
      s.onload = () => {
        window.instgrm?.Embeds.process();
        setTimeout(applyTitles, 1500);
      };
      document.body.appendChild(s);
    }
  }, [visible]);

  return (
    <section ref={sectionRef} className="py-16 md:py-20 bg-accent/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Instagram className="h-6 w-6 text-primary" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold">
              Follow Us on Instagram
            </h2>
          </div>
          <p className="text-muted-foreground">
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary transition-colors"
            >
              {INSTAGRAM}
            </a>
          </p>
        </div>

        {/* Vertical stack on mobile, horizontal row on desktop */}
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-4 justify-center items-start max-w-6xl mx-auto">
          {INSTAGRAM_POSTS.map((url, i) => (
            <div
              key={i}
              className="w-full lg:flex-1 lg:max-w-sm flex justify-center"
            >
              <blockquote
                className="instagram-media"
                data-instgrm-captioned
                data-instgrm-permalink={url}
                data-instgrm-version="14"
                style={{
                  background: "#FFF",
                  border: 0,
                  borderRadius: 3,
                  boxShadow:
                    "0 0 1px 0 rgba(0,0,0,0.5), 0 1px 10px 0 rgba(0,0,0,0.15)",
                  margin: 0,
                  maxWidth: 540,
                  minWidth: 280,
                  padding: 0,
                  width: "100%",
                }}
              />
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Button asChild variant="outline" size="lg" className="max-w-full h-auto min-h-12 py-3 px-4 whitespace-normal tracking-normal">
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">
              <Instagram className="h-5 w-5 mr-2" /> Follow {INSTAGRAM}
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default InstagramEmbeds;
